/**
 * Synchro de la liste des employes (GET /caisse/employees/roster, PR 1.2) :
 * au demarrage avec celle du catalogue, et sur demande.
 *
 * Le serveur renvoie les employes ACTIFS rattaches au magasin du poste, avec
 * leurs droits, sans aucun NIP. Sur le poste, dans UNE transaction :
 *   - un employe absent de la liste (inactif, change de magasin, supprime)
 *     est revoque : son verificateur est efface, il ne se connecte plus,
 *     meme hors ligne. Une liste vide revoque donc tout le monde ;
 *   - un employe de la liste sans NIP dans l'ERP (has_pin = false) perd aussi
 *     son verificateur ;
 *   - par prudence, une entree qui porterait un statut autre que ACTIVE ou un
 *     autre magasin (champs que le serveur ne renvoie pas aujourd'hui) est
 *     traitee comme absente ;
 *   - les autres : nom et droits (can_discount, can_override_price,
 *     can_close_shift, is_manager...) remplaces par ceux du serveur ;
 *   - un employe nouveau est ajoute sans verificateur : il apparait dans
 *     « Employés à connecter une fois en ligne ».
 */
import { requestToPromise, transactionDone } from '../store/idb';
import { normalizeRights } from '../store/employees';
import { putMeta } from '../store/meta';
import { META_KEYS, STORES } from '../store/schema';
import { codeForApiError } from './catalogSync';

/** L'entree du serveur rattache-t-elle bien l'employe, actif, a ce magasin ? */
function isActiveHere(entry, storeId) {
    if (entry.status !== undefined && entry.status !== 'ACTIVE') return false;
    if (entry.store_id !== undefined && entry.store_id !== storeId) return false;
    return true;
}

/**
 * Applique une liste recue au magasin `employees` (fonction testable sans reseau).
 * @returns {Promise<{ revoked: number[], updated: number, added: number }>}
 */
export async function applyRoster(db, { roster, storeId, now = new Date() }) {
    const at = now.toISOString();
    const active = new Map();
    for (const entry of roster) {
        if (isActiveHere(entry, storeId)) active.set(entry.employee_id, entry);
    }

    const tx = db.transaction(STORES.EMPLOYEES, 'readwrite');
    const store = tx.objectStore(STORES.EMPLOYEES);
    const done = transactionDone(tx);
    const cached = await requestToPromise(store.getAll());
    const revoked = [];
    let updated = 0;

    for (const employee of cached) {
        const entry = active.get(employee.employee_id);
        if (!entry) {
            if (!employee.revoked || employee.pin_verifier) revoked.push(employee.employee_id);
            store.put({
                ...employee,
                pin_verifier: null,
                pin_kdf: null,
                revoked: true,
                revoked_at: employee.revoked ? employee.revoked_at : at,
                revoked_reason: 'ABSENT_DE_LA_LISTE',
                roster_synced_at: at,
            });
            continue;
        }
        const hasPin = entry.has_pin === true;
        store.put({
            ...employee,
            display_name: entry.display_name,
            has_pin: hasPin,
            rights: normalizeRights(entry.rights),
            pin_verifier: hasPin ? employee.pin_verifier || null : null,
            pin_kdf: hasPin ? employee.pin_kdf || null : null,
            revoked: false,
            revoked_at: null,
            revoked_reason: null,
            roster_synced_at: at,
        });
        active.delete(employee.employee_id);
        updated += 1;
    }

    let added = 0;
    for (const entry of active.values()) {
        store.put({
            employee_id: entry.employee_id,
            display_name: entry.display_name,
            has_pin: entry.has_pin === true,
            rights: normalizeRights(entry.rights),
            pin_verifier: null,
            pin_kdf: null,
            last_online_login_at: null,
            revoked: false,
            revoked_at: null,
            revoked_reason: null,
            roster_synced_at: at,
        });
        added += 1;
    }
    await done;
    return { revoked, updated, added };
}

/**
 * @returns {Promise<{ ok: true, revoked: number[], updated: number, added: number } | { ok: false, code: string }>}
 */
export async function syncRoster({ db, api, token, storeId, now = () => new Date() }) {
    let roster;
    try {
        roster = await api.getRoster(token);
    } catch (error) {
        return { ok: false, code: codeForApiError(error) };
    }
    const at = now();
    const result = await applyRoster(db, { roster, storeId, now: at });
    await putMeta(db, { [META_KEYS.ROSTER_SYNC]: { synced_at: at.toISOString(), count: roster.length } });
    return { ok: true, ...result };
}
